import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import LandingSearchForm from './LandingSearchForm/LandingSearchForm';
import { Heading, Icons, Modal, NamedLink } from '../../../components';
import axios from 'axios';

import { types as sdkTypes } from '../../../util/sdkLoader';
import css from './SectionHero.module.css';
import { useSelector } from 'react-redux';
import { currentUserSelector } from '../../../ducks/user.duck';
import { getUserBoatDetails } from '../../../util/dataExtractors';
import { MAX_BOAT_SIZE } from '../../../util/constants';
import { SearchType } from '../../../util/enums';
import { isMobileViewport } from '../../../util/viewport';
import { parse, stringify } from '../../../util/urlHelpers';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const { LatLng, LatLngBounds } = sdkTypes;

function SectionHero(props) {
  const { history, viewport, onManageDisableScrolling } = props;
  const location = useLocation();
  const currentUser = useSelector(currentUserSelector);
  const boatDetails = getUserBoatDetails(currentUser);
  const boatLength = boatDetails?.length;
  const isMobileLayout = isMobileViewport(viewport);
  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [isMobileSearchModalOpen, setIsMobileSearchModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const submitCalledRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const keywords = [
    {
      name: intl.formatMessage({
        id: 'SectionHero.mooringBuoy',
      }),
      search: 'pub_category=mooringBuoy',
    },
    {
      name: intl.formatMessage({
        id: 'SectionHero.privateDock',
      }),
      search: 'pub_category=privateDock',
    },
    // {
    //   name: intl.formatMessage({
    //     id: 'SectionHero.publicMoorage',
    //   }),
    //   search: 'pub_category=public_moorage',
    // },
  ];
  const handleSubmit = useCallback(
    async values => {
      // 1) Prevent double‐submit
      if (submitCalledRef.current) {
        return;
      }
      submitCalledRef.current = true;
      setSubmitInProgress(true);

      try {
        // 2) Destructure form values
        const { searchType = '', keywords = '', location = {} } = values;

        const { search: addressInput = '', selectedPlace = {} } = location;

        const { bounds: initialBounds } = selectedPlace || {};

        // 3) Build query
        let searchQuery = {};

        if (searchType === SearchType.KEYWORDS) {
          searchQuery.keywords = keywords;
        } else {
          let address = addressInput;
          let bounds = initialBounds;

          if (!bounds) {
            try {
              const { city, latitude, longitude } = (
                await axios.get(process.env.REACT_APP_IPAPI_URL)
              ).data;
              address = city || address;
              bounds = new LatLngBounds(
                new LatLng(latitude + 0.01, longitude + 0.01),
                new LatLng(latitude - 0.01, longitude - 0.01)
              );
            } catch (err) {
              console.error('Geolocation fallback failed:', err);
            }
          }

          if (bounds) {
            searchQuery.bounds = bounds;
          }
        }

        // 4) Add boat‐length filter
        if (boatLength) {
          searchQuery.pub_maxBoatSize = `${Math.round(boatLength)},${MAX_BOAT_SIZE}`;
        }

        // 5) Navigate once
        if (isMobileSearchModalOpen) {
          setIsMobileSearchModalOpen(false);
          onManageDisableScrolling('SectionHero.SearchModal', false);
        }

        const path = createResourceLocatorString('SearchPage', routeConfiguration, {}, searchQuery);
        history.push(path, { searchType });
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(() => {
            window.scrollTo(0, 0);
          });
        }
      } finally {
        // 6) Reset guard & spinner
        submitCalledRef.current = false;
        setSubmitInProgress(false);
      }
    },
    [history, routeConfiguration, boatLength, isMobileSearchModalOpen, onManageDisableScrolling]
  );

  const title = intl.formatMessage({ id: 'SectionHero.title' });
  const description = intl.formatMessage({
    id: 'SectionHero.description',
  });
  const viewAvailable = intl.formatMessage({
    id: 'SectionHero.viewAvailable',
  });
  const { keywords: initialKeywords } = parse(location?.search || '');
  const searchInitialValues = {
    searchType: SearchType.KEYWORDS,
    keywords: initialKeywords || '',
  };
  const redirectToURLWithModalState = modalStateParam => {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
    history.push(`${pathname}${searchString}`, state);
  };

  const handleMobileSearchOpen = () => {
    redirectToURLWithModalState('mobilesearch');
  };
  const heroContent = (
    <div className={css.heroContent}>
      <Heading as="h1" rootClassName={css.heading}>
        {title}
      </Heading>
      <p>{description}</p>
      <div className={css.heroSearchSec}>
        {isMobileLayout ? (
          <div className={css.mobileSearch} onClick={() => handleMobileSearchOpen()}>
            <Icons name="searchIcon" /> {intl.formatMessage({ id: 'SectionHero.startSearch' })}
          </div>
        ) : (
          <LandingSearchForm
            initialValues={searchInitialValues}
            rootClassName={css.landingSearchForm}
            onSubmit={handleSubmit}
            submitInProgress={submitInProgress}
            intl={intl}
            viewport={viewport}
          />
        )}
      </div>
      <div className={css.bottomContent}>
        <div className={css.keywords}>
          <span className={css.keyword}>{viewAvailable}</span>
          {keywords.map((keyword, index) => (
            <NamedLink
              key={index}
              className={css.keyword}
              name="SearchPage"
              to={{ search: keyword?.search }}
            >
              {keyword.name}
            </NamedLink>
          ))}
        </div>
      </div>
      <Modal
        id="SectionHero.SearchModal"
        isOpen={isMobileSearchModalOpen}
        onClose={() => {
          setIsMobileSearchModalOpen(false);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <LandingSearchForm
          initialValues={searchInitialValues}
          rootClassName={css.landingSearchForm}
          onSubmit={handleSubmit}
          submitInProgress={submitInProgress}
          intl={intl}
          viewport={viewport}
          isMobileSearch={true}
          setIsMobileSearchModalOpen={setIsMobileSearchModalOpen}
        />
      </Modal>
    </div>
  );

  return (
    <div className={css.heroContainer}>
      <div className={css.contentWidth}>{heroContent}</div>
    </div>
  );
}

export default SectionHero;
